import './list-view.scss'
import {observer} from 'mobx-react-lite'
import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import {DragGhost} from '@components/common/sandbox/list-view/components/drag-ghost'
import {DropPlaceholder} from '@components/common/sandbox/list-view/components/list-drop-placeholder'
import {Rows} from '@components/common/sandbox/list-view/components/list-rows'
import {configuredSort} from '@components/common/sandbox/list-view/helpers/configuredSort'
import {
  ColumnVisiblity,
  Identifiable,
  ListStoreExposedApi,
  ListViewProps,
  Placement,
  SortSource
} from '@components/common/sandbox/list-view/types'
import {cn} from '@containers/html-utils'
import {Col} from './components/col'
import {ListContext, ListViewStore} from './list-store'
import * as Tw from './tw-components'

// Вспомогательная функция для создания дженерик-компонента с forwardRef
const InternalListVIew = <TData extends Identifiable>(
  props: ListViewProps<TData>,
  ref: React.Ref<ListStoreExposedApi>
) => {
  const [store] = useState(() => new ListViewStore(props))
  const {
    columns,
    rows,
    enabledColumnIds,
    className,
    order,
    sort,
    markAsDropTarget,
    hidden
  } = props

  const orderMap = useMemo(
    () => (order ? new Map(order.map((id, index) => [id, index])) : null),
    [order]
  )
  const doneMap = useMemo(
    () => new Map(rows.map(row => [row.data.id, store.isDone(row)])),
    [rows]
  )

  const sortedRows = useMemo(() => {
    /* Clean up temporary created items */
    store.cleanupTemporarilyCreatedItems(null, rows)

    /* Sort by custom order */
    if (orderMap && (!sort || sort.type === SortSource.ByCustomOrder)) {
      return rows
        ? [...rows].sort((a, b) => {
            const doneA = doneMap.get(a.data.id)
            const doneB = doneMap.get(b.data.id)
            if (doneA !== doneB) {
              /**
               * If one of the tasks is done, it should be placed at the end of the list.
               * True in numerical form is 1 and False is 0, so we can just subtract the done values.
               */
              return Number(doneA) - Number(doneB)
            }
            return (
              (orderMap.get(a.data.id) ?? 100000) -
              (orderMap.get(b.data.id) ?? 100000)
            )
          })
        : []
    }

    /* Sort by column */
    if (sort && sort.type === SortSource.ByColumn) {
      const column = columns.find(col => col.id === sort.columnId)
      if (!column || !column.sort) {
        return rows
      }
      return configuredSort(rows, column.sort, sort.asc, doneMap)
    }

    /* No sort at all */
    return rows
  }, [rows, orderMap, columns, sort])

  /* Filter columns according to its enable status and visibility */
  const filteredColumns = useMemo(() => {
    const filteringByVisibility = columns.filter(
      col => col.visibility !== ColumnVisiblity.AlwaysHidden
    )
    return enabledColumnIds
      ? filteringByVisibility.filter(col => enabledColumnIds.includes(col.id))
      : filteringByVisibility
  }, [columns, enabledColumnIds])

  useImperativeHandle(ref, () => store.exposedApi)

  useLayoutEffect(() => {
    store.updateFromProps(props)
  }, [props])

  const [gridTemplateColumns] = useMemo(
    () => store.createGridTemplate(filteredColumns, 0),
    [filteredColumns]
  )

  return (
    <ListContext.Provider value={store}>
      {!hidden ? (
        <Tw.TwListView
          className={cn(
            className,
            store.draggedId !== null && !store.nonInteractive
              ? ''
              : 'listview-hoverable-group'
          )}
          onMouseMove={store.onMouseMoveOverList}
          onDragOverCapture={store.onMouseMoveOverList}
        >
          {sortedRows.length > 0 && (
            <Tw.TwListViewHeader
              style={{
                gridTemplateColumns
              }}
            >
              <Col title={store.primaryColumnTitle} />
              {filteredColumns.map((col, i) => (
                <Col key={i} title={col.shortName} />
              ))}
            </Tw.TwListViewHeader>
          )}
          <Tw.TwBody
            ref={store.setBodyElement}
            onDragOver={store.onDragOverBody}
            $nonInteractive={store.nonInteractive}
          >
            <DropPlaceholder
              forceShow={
                markAsDropTarget && markAsDropTarget !== Placement.None
              }
            />
            <Rows
              parentId={null}
              data={sortedRows}
              nestedLevel={0}
              columns={filteredColumns}
            />
          </Tw.TwBody>
        </Tw.TwListView>
      ) : (
        <DropPlaceholder
          forceShow={markAsDropTarget && markAsDropTarget !== Placement.None}
          isStatic
        />
      )}
      <DragGhost />
    </ListContext.Provider>
  )
}

interface ListViewWithRefProps {
  /**
   * Reference to the list store
   */
  ref?: React.Ref<ListStoreExposedApi>
}

// Оборачиваем createListView в forwardRef, чтобы передать ref и дженерик
export const ListView = observer(forwardRef(InternalListVIew)) as <
  TData extends Identifiable
>(
  props: ListViewProps<TData> & ListViewWithRefProps
) => React.ReactElement | null
